import type React from 'react'
import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useAuthStore } from '@/stores/auth-store'
import { getInvitationByToken, acceptInvitation } from '@/lib/org-api-client'
import { Button } from '@knownow/ui'

interface InvitationDetails {
  readonly organizationId: string
  readonly role: string
  readonly status: string
}

export function InvitePage(): React.ReactElement {
  const { token } = useParams<{ token: string }>()
  const navigate = useNavigate()
  const user = useAuthStore((s) => s.user)
  const [invitation, setInvitation] = useState<InvitationDetails | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [accepting, setAccepting] = useState(false)

  useEffect(() => {
    if (!token) return

    // If not authenticated, store token and redirect to login
    if (!user) {
      localStorage.setItem('knownow_invite_token', token)
      navigate('/login')
      return
    }

    // Fetch invitation details
    getInvitationByToken(token)
      .then((data) => {
        setInvitation({
          organizationId: data.organizationId,
          role: data.role,
          status: data.status,
        })
        setLoading(false)
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : 'Failed to load invitation')
        setLoading(false)
      })
  }, [token, user, navigate])

  const handleAccept = async (): Promise<void> => {
    if (!token) return
    setAccepting(true)
    setError(null)

    try {
      await acceptInvitation(token)
      localStorage.removeItem('knownow_invite_token')
      navigate('/')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to accept invitation')
      setAccepting(false)
    }
  }

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center" data-testid="invite-loading">
        <p className="text-neutral-500">Loading invitation...</p>
      </div>
    )
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-neutral-50" data-testid="invite-page">
      <div className="w-full max-w-sm space-y-6 rounded-lg bg-white p-8 shadow-md text-center">
        <h1 className="text-display-sm font-serif">You&apos;re invited</h1>

        {error && (
          <div className="rounded-md bg-error-50 p-3 text-sm text-error-700">{error}</div>
        )}

        {invitation && (
          <>
            <p className="text-sm text-neutral-600">
              You&apos;ve been invited to join as <strong>{invitation.role}</strong>.
            </p>
            <Button onClick={handleAccept} disabled={accepting} data-testid="accept-invite">
              {accepting ? 'Accepting...' : 'Accept invitation'}
            </Button>
          </>
        )}
      </div>
    </div>
  )
}
